import type { MaybeRef, Ref } from 'vue';
import { computed, unref } from 'vue';

export interface ChartSeries {
  name: string;
  data: number[];
}

export interface ChartData {
  categories: string[];
  series: ChartSeries[];
}

type SortFunction = (a: [string, number], b: [string, number]) => number;

/**
 * Composable to transform and sort record data into chart-compatible categories and series.
 *
 * @param dataInput - A reactive reference (ref, computed) or a plain object Record<string, number>. Can be null or undefined.
 * @param sortFn - A function to sort the [key, value] pairs from the dataInput.
 * @param seriesName - Optional name for the data series (reactive or plain string). Defaults to 'Series'.
 * @param categoryMapper - Optional function to transform the key into a category label. Defaults to returning the key itself.
 * @returns A computed ref containing the structured chart data { categories: string[], series: { name: string, data: number[] }[] }.
 */
export function useChartData(
  dataInput: MaybeRef<Record<string, number> | null | undefined>,
  sortFn: SortFunction,
  seriesName: MaybeRef<string> = 'Series',
  categoryMapper: (key: string, value: number) => string = (key) => key,
): Readonly<Ref<ChartData>> {
  return computed(() => {
    const currentData = unref(dataInput);
    const currentSeriesName = unref(seriesName);

    if (!currentData) {
      return { categories: [], series: [] };
    }

    const sortedEntries = Object.entries(currentData).sort(sortFn);

    const categories = sortedEntries.map(([key, value]) =>
      categoryMapper(key, value),
    );
    const dataValues = sortedEntries.map(([, value]) => value);

    return {
      categories,
      series: [
        {
          name: currentSeriesName,
          data: dataValues,
        },
      ],
    };
  });
}
